import type { ReactNode } from 'react'
import { Badge } from '../base/ui.tsx'
import { Icon } from '../icons.tsx'
import { formatDelta, formatValue, type Format } from '../spec/format.ts'
import { readPath } from '../spec/schema.ts'

/**
 * One number that matters: its label, the value big, how it changed (green when it went the `better` way, red when
 * not), a caption, and a small line of recent values.
 */
export function Metric({
  label,
  value,
  format,
  unit,
  delta,
  better = 'higher',
  caption,
  trend,
}: {
  label: ReactNode
  value: string | number | boolean | null
  format?: Format | null
  unit?: string | null
  delta?: number | null
  better?: 'higher' | 'lower'
  caption?: ReactNode
  trend?: readonly number[] | null
}) {
  const good = delta == null || delta === 0 ? null : delta > 0 === (better === 'higher')
  return (
    <div className="ui-metric">
      <p className="ui-metric-label">{label}</p>
      <p className="ui-metric-value-row">
        <span className="ui-metric-value">{formatValue(value, format, unit ?? '')}</span>
        {delta != null && (
          <span className="ui-metric-delta" data-good={good === null ? undefined : String(good)}>
            <Icon name={delta > 0 ? 'trend-up' : delta < 0 ? 'trend-down' : 'steady'} size={14} />
            {formatDelta(delta, format, unit ?? '')}
          </span>
        )}
      </p>
      {caption && <p className="ui-metric-caption">{caption}</p>}
      {trend && trend.length > 1 && <Sparkline values={trend} />}
    </div>
  )
}

/** A small line of values, oldest first, the last one marked. Decorative: the numbers are said elsewhere. */
export function Sparkline({ values }: { values: readonly number[] }) {
  const W = 120
  const H = 28
  const lo = Math.min(...values)
  const hi = Math.max(...values)
  const x = (i: number) => 2 + (i / (values.length - 1)) * (W - 4)
  const y = (v: number) => 3 + (1 - (v - lo) / (hi - lo || 1)) * (H - 6)
  const last = values.length - 1
  return (
    <svg className="ui-sparkline" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden>
      <polyline points={values.map((v, i) => `${x(i)},${y(v)}`).join(' ')} vectorEffect="non-scaling-stroke" />
      <circle cx={x(last)} cy={y(values[last]!)} r="2.5" />
    </svg>
  )
}

export interface Column {
  /** A dot path into each row: "score", "player.name". */
  key: string
  label: ReactNode
  format?: Format | null
  unit?: string | null
  align?: 'start' | 'end' | null
}

/** Rows of records in columns, numbers right-aligned. */
export function DataTable({
  columns,
  rows,
  limit,
  empty = 'Nothing yet.',
}: {
  columns: readonly Column[]
  rows: readonly Record<string, unknown>[]
  limit?: number | null
  empty?: ReactNode
}) {
  if (!rows.length) return <p className="ui-data-table-empty">{empty}</p>
  const shown = limit ? rows.slice(0, limit) : rows
  const cell = (row: Record<string, unknown>, key: string) => readPath(row, key)
  const alignOf = (c: Column) => c.align ?? (shown.some((r) => typeof cell(r, c.key) === 'number') ? 'end' : 'start')
  return (
    <div className="ui-data-table-wrap">
      <table className="ui-data-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} data-align={alignOf(c)}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.map((row, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c.key} data-align={alignOf(c)}>
                  {formatValue(cell(row, c.key), c.format, c.unit ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {limit && rows.length > limit && (
        <p className="ui-data-table-more">
          Showing {limit} of {rows.length.toLocaleString('en')}
        </p>
      )}
    </div>
  )
}

export interface RowItem {
  title: ReactNode
  detail?: ReactNode
  meta?: ReactNode
  badge?: ReactNode
  href?: string | null
}

/** A list of rows (name, detail, a note at the right); rows with an `href` are links. */
export function RowList({ items, empty = 'Nothing yet.' }: { items: readonly RowItem[]; empty?: ReactNode }) {
  if (!items.length) return <p className="ui-data-table-empty">{empty}</p>
  return (
    <ul className="rows">
      {items.map((item, i) => {
        const inner = (
          <>
            <span className="row-name">
              {item.title}
              {item.badge && (
                <>
                  {' '}
                  <Badge>{item.badge}</Badge>
                </>
              )}
            </span>
            <span className="row-detail">{item.detail}</span>
            <span className="row-time">{item.meta}</span>
          </>
        )
        return item.href ? (
          <li key={i}>
            <a className="row row-link" href={item.href}>
              {inner}
            </a>
          </li>
        ) : (
          <li key={i} className="row">
            {inner}
          </li>
        )
      })}
    </ul>
  )
}
